import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getDeviceIdFromSession } from '@ibk/shared-auth';
import { attemptTokenRefresh, config, ibkApi, selectAuthorizationHeader } from '@ibk/shared-config';
function validPage(result) {
    return !!result && Array.isArray(result.data) && Number.isSafeInteger(result.total) && result.total >= 0;
}
function validNextPage(batch, total, seen) {
    return (validPage(batch) && batch.total === total && batch.data.length > 0 && !batch.data.some(item => seen.has(item.id)));
}
function validInstallment(installment) {
    return (Number.isFinite(installment.amount) &&
        installment.amount >= 0 &&
        typeof installment.dueDate === 'string' &&
        /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(installment.dueDate) &&
        Number.isFinite(Date.parse(installment.dueDate)));
}
function aggregatePayables(items, today) {
    const end = new Date(`${today}T12:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 7);
    const seven = end.toISOString().slice(0, 10);
    const totals = { hoje: 0, sete: 0, atraso: 0 };
    if (items.some(item => !Array.isArray(item.installments)))
        return null;
    for (const installment of items.flatMap(item => item.installments)) {
        if (['PAID', 'CANCELLED'].includes(installment.status))
            continue;
        if (!validInstallment(installment))
            return null;
        const due = installment.dueDate.slice(0, 10);
        if (due < today)
            totals.atraso += installment.amount;
        else if (due === today)
            totals.hoje += installment.amount;
        else if (due <= seven)
            totals.sete += installment.amount;
    }
    return totals;
}
export const bpoApi = ibkApi.injectEndpoints({
    endpoints: builder => ({
        getBpoPayables: builder.query({
            async queryFn({ accountId, today }, api) {
                const query = fetchBaseQuery({ baseUrl: config.apiEscritorioPersonAccess, timeout: 30000 });
                const execute = (page) => query({
                    url: '/v1/accounts-payable-management',
                    params: { page, pageSize: 100 },
                    headers: {
                        Authorization: selectAuthorizationHeader(api.getState()) ?? '',
                        'account-id': accountId,
                        'device-id': getDeviceIdFromSession() ?? '',
                        'X-Application-Name': 'internet-banking-client',
                    },
                }, api, {});
                const fetchPage = async (page) => {
                    let response = await execute(page);
                    if (response.error?.status === 401 && (await attemptTokenRefresh(api)))
                        response = await execute(page);
                    return response;
                };
                const response = await fetchPage(1);
                if (response.error)
                    return { error: response.error };
                const result = response.data.result;
                if (!validPage(result))
                    return { error: { status: 'CUSTOM_ERROR', error: 'BPO_PAYABLES_INCOMPLETE' } };
                const items = [...result.data];
                const seen = new Set(items.map(item => item.id));
                let page = 2;
                while (items.length < result.total) {
                    if (api.signal.aborted)
                        return { error: { status: 'CUSTOM_ERROR', error: 'BPO_PAYABLES_ABORTED' } };
                    const next = await fetchPage(page);
                    if (next.error)
                        return { error: next.error };
                    const batch = next.data.result;
                    if (!validNextPage(batch, result.total, seen))
                        return { error: { status: 'CUSTOM_ERROR', error: 'BPO_PAYABLES_INCOMPLETE' } };
                    batch.data.forEach(item => seen.add(item.id));
                    items.push(...batch.data);
                    page++;
                }
                if (items.length !== result.total)
                    return { error: { status: 'CUSTOM_ERROR', error: 'BPO_PAYABLES_INCOMPLETE' } };
                const totals = aggregatePayables(items, today);
                if (totals === null)
                    return { error: { status: 'CUSTOM_ERROR', error: 'BPO_PAYABLES_INVALID' } };
                return { data: totals };
            },
            keepUnusedDataFor: 0,
        }),
        getBpoSummary: builder.query({
            async queryFn(params, _api, _extraOptions, baseQuery) {
                const ids = params.companies.split(',').filter(Boolean);
                const result = [];
                for (let offset = 0; offset < ids.length; offset += 50) {
                    const response = await baseQuery({
                        url: '/v1/bpo/resumo',
                        params: { month: params.month, companies: ids.slice(offset, offset + 50).join(',') },
                    });
                    if (response.error)
                        return { error: response.error };
                    result.push(...response.data.result);
                }
                return { data: { status: 200, result } };
            },
            keepUnusedDataFor: 0,
        }),
    }),
});
export const { useGetBpoSummaryQuery, useGetBpoPayablesQuery } = bpoApi;
